import { useMemo } from 'react';
import { orderBy } from 'lodash';
import { formatDate } from '@shared/services/dateService';
import GET_AVAILABLE_ATLS from '../queries/getAvailableAtlsQuery';
import GET_CURRENT_ATL from '../queries/getCurrentAtlQuery';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';

const ATLS_FIELDS = {
  ATL: 'atl',
  ATL_OBSERVER: 'atlObserver'
};

const useAvailableAtlList = (
  eventStartDate,
  eventEndDate,
  selectedAtl,
  selectedAtlObserver,
  setEventFormData,
  eventId,
  isAutoAssignATL,
  skip = false
) => {
  const clearUnavailableSelection = (availableAtls, selectedUser, fieldName) => {
    if (!availableAtls.some(x => x.ExternalUserId === selectedUser?.value)) {
      setEventFormData(prevState => ({ ...prevState, [fieldName]: null }));
    }
  };

  const {
    data: availableAtlsData,
    backgroundLoading: availableAtlsLoading,
    error: availableAtlsError
  } = useCacheNetworkQuery(GET_AVAILABLE_ATLS, {
    variables: {
      start: formatDate(eventStartDate),
      end: formatDate(eventEndDate),
      eventId: eventId || undefined
    },
    skip: !eventEndDate || skip || isAutoAssignATL,
    onCompleted: result => {
      const allAvailableAtls = result.availableAtls.AtlCandidates.concat(result.availableAtls.Atls);
      clearUnavailableSelection(allAvailableAtls, selectedAtl, ATLS_FIELDS.ATL);
      clearUnavailableSelection(allAvailableAtls, selectedAtlObserver, ATLS_FIELDS.ATL_OBSERVER);
    }
  });

  const {
    data: currentAtlData,
    backgroundLoading: currentAtlDataLoading,
    error: currentAtlDataError
  } = useCacheNetworkQuery(GET_CURRENT_ATL, {
    skip: !isAutoAssignATL,
    onCompleted: result => {
      setEventFormData(prevState => ({
        ...prevState,
        atl: {
          value: result.currentAtl.ExternalUserId,
          label: `${result.currentAtl.UserLicense.Name} - ${result.currentAtl.CurrentRole.Title}`,
          currentRole: result.currentAtl.CurrentRole.Value
        }
      }));
    }
  });

  const atlList = useMemo(() => {
    if (isAutoAssignATL) {
      return currentAtlData ? [currentAtlData.currentAtl] : [];
    }

    if (!availableAtlsData) {
      return [];
    }
    return orderBy(
      availableAtlsData.availableAtls.AtlCandidates.concat(availableAtlsData.availableAtls.Atls),
      [atl => atl.UserLicense.Name.toLowerCase(), atl => atl.CurrentRole.Title]
    );
  }, [currentAtlData, availableAtlsData, isAutoAssignATL]);

  return {
    availableAtlList: atlList || [],
    availableAtlsLoading: availableAtlsLoading || currentAtlDataLoading,
    availableAtlsError: availableAtlsError || currentAtlDataError
  };
};

export default useAvailableAtlList;
